import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import moment from "moment";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import generatedFileServices from "app/services/automations/generated-file-services";
import DataExportDialog from "app/shared/downloads/DataExportDialog";
import { canExportData } from "app/shared/downloads/permissions";
import { StandardPageHeader } from "app/shared/ui";

const STATUS = {
    pending: { label: "En cola", color: "default" },
    processing: { label: "Preparando", color: "info" },
    ready: { label: "Lista", color: "success" },
    failed: { label: "No se pudo generar", color: "error" },
    expired: { label: "Vencida", color: "default" },
};
const POLL_MS = 4000;

const formatSize = (bytes) => {
    if (!bytes) return null;
    return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const FileRow = ({ file, onDownload, downloading }) => {
    const status = STATUS[file.status] || STATUS.pending;
    const Icon = file.kind === "report" ? PictureAsPdfOutlinedIcon : TableChartOutlinedIcon;
    const details = [
        `Pedida ${moment(file.created_at).format("DD-MM-YYYY HH:mm")}`,
        file.kind === "device_data" && file.rows_count != null && `${file.rows_count.toLocaleString("es-CL")} lecturas`,
        formatSize(file.byte_size),
        file.status === "ready" && file.expires_at && `disponible hasta el ${moment(file.expires_at).format("DD-MM-YYYY")}`,
    ].filter(Boolean);

    return (
        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <Icon color={file.kind === "report" ? "error" : "success"} />
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                            <Typography variant="body1" fontWeight={600} sx={{ wordBreak: "break-word" }}>{file.title}</Typography>
                            <Chip size="small" label={status.label} color={status.color} variant={file.status === "ready" ? "filled" : "outlined"}
                                  icon={["pending", "processing"].includes(file.status) ? <CircularProgress size={12} sx={{ ml: "6px !important" }} /> : undefined} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">{details.join(" · ")}</Typography>
                        {file.status === "failed" && file.error_message && (
                            <Typography variant="caption" color="error.main" display="block">{file.error_message}</Typography>
                        )}
                    </Box>
                </Stack>
                {file.downloadable && (
                    <Button variant="outlined" size="small" startIcon={<DownloadOutlinedIcon />} disabled={downloading} onClick={() => onDownload(file)}>
                        Descargar
                    </Button>
                )}
            </Stack>
        </Paper>
    );
};

// «Descargas»: los datos de los locales y los reportes que pidió el usuario. Se actualiza sola mientras algo se prepara.
const DownloadsIndex = () => {
    const { authUser } = useJumboAuth();
    const [files, setFiles] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [notice, setNotice] = useState(null);

    const load = useCallback(() => generatedFileServices.list()
        .then((resp) => { setFiles(resp?.data || []); setMeta(resp?.meta || null); })
        .catch(() => {})
        .finally(() => setLoading(false)), []);

    useEffect(() => { load(); }, [load]);

    const preparing = files.some((file) => ["pending", "processing"].includes(file.status));
    useEffect(() => {
        if (!preparing) return undefined;
        const timer = setInterval(load, POLL_MS);
        return () => clearInterval(timer);
    }, [preparing, load]);

    const download = async (file) => {
        setDownloadingId(file.id);
        try {
            await generatedFileServices.download(file);
        } finally {
            setDownloadingId(null);
        }
    };

    const exportEnabled = canExportData(authUser) && !!meta?.data_export;

    return (
        <Box>
            <StandardPageHeader
                title="Descargas"
                subtitle={`Los datos de tus locales y los reportes que pediste. Cada archivo se borra a los ${meta?.keep_days || 7} días.`}
                actions={exportEnabled && (
                    <Button variant="contained" startIcon={<DownloadOutlinedIcon />} onClick={() => setDialogOpen(true)}>
                        Descargar datos de un local
                    </Button>
                )}
            />
            {exportEnabled && (
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                    Tus datos se conservan {meta.data_export.retention_days} días en Xpit Connect. Si necesitas guardarlos por más tiempo,
                    escríbenos a soporte@xpit.cl y te contamos cómo ampliar la retención.
                </Alert>
            )}
            {!!notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice}</Alert>}

            {loading ? (
                <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box>
            ) : files.length ? (
                <Stack spacing={1.25}>
                    {files.map((file) => (
                        <FileRow key={file.id} file={file} onDownload={download} downloading={downloadingId === file.id} />
                    ))}
                </Stack>
            ) : (
                <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2.5 }}>
                    <Typography variant="body1" fontWeight={600}>Todavía no tienes descargas</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {exportEnabled ? "Pide los datos de un local o genera un reporte: aparecerán aquí." : "Los reportes que generes aparecerán aquí."}
                    </Typography>
                </Paper>
            )}

            <DataExportDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onRequested={(file) => {
                    setNotice(`Estamos preparando «${file.title}». Te avisaremos por correo cuando esté lista.`);
                    load();
                }}
            />
        </Box>
    );
};

export default DownloadsIndex;
